import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useMemo} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useConstant} from '../../../../../srcCommon/helpers/hookHelpers';
import {PreviewBuilderModel, PreviewerLinkTypesEnum} from '../../../../models/api/previewerModel';
import {StringOrTemplate} from '../../../editors/commons/stringTemplateModel';
import {getExpressionTypeIcon} from '../../../editors/expressions/expressionIcons';
import {Expression} from '../../../editors/expressions/expressionModel';
import {buildStringTemplateParameter} from '../../../editors/expressions/expressionParamHelpers';
import {isExpressionRef, TeammateIdOrRef} from '../../../editors/expressions/expressionRefModel';
import {StringLikeResults, TopicResult} from '../../../editors/expressions/infer/expressionResultModel';
import {useOnSelectCreateExpressionRef} from '../../../rules/ruleEditor/hooks/useOnSelectCreateExpression';
import {InputStringTemplateParameter} from '../../../rules/ruleEditor/inputs/inputStringTemplateParameter';
import {TeammateParameter} from '../../../rules/ruleEditor/parameters/teammateParameter';
import {RuleMultilineParameterCard} from '../../../rules/ruleEditor/ruleMultilineParameterCard';
import {appendExpressionRef} from '../../../rules/stringTemplateHelpers';
import {PreviewerLinkTypeInput} from '../inputs/previewerLinkTypeInput';

/*
 * Props.
 */

interface PreviewerBuilderCardProps {
  expressions: ReadonlyArray<Expression>;
  linkType: PreviewerLinkTypesEnum;
  onUpdateLinkType: HandlerOf<PreviewerLinkTypesEnum>;
  previewBuilder: PreviewBuilderModel;
  onUpdatePreviewBuilder: HandlerOf<PreviewBuilderModel>;
  isReadOnly: boolean;
}

/*
 * Style.
 */

const StyledParameterLineDiv = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledParameterTitleLineDiv = styled.div`
  display: flex;
  margin-bottom: 5px;

  :not(:first-child) {
    margin-left: 5px;
  }

  i {
    color: ${(p) => p.theme.palette.red.shade40};
    font-style: normal;
  }
`;

/*
 * Component.
 */

export const PreviewerBuilderCard: FC<PreviewerBuilderCardProps> = (props) => {
  const theme = useUserTheme();
  const {expressions, linkType, onUpdateLinkType, previewBuilder, onUpdatePreviewBuilder, isReadOnly} = props;

  /*
   * Name.
   */

  const nameParam = useConstant(() => buildStringTemplateParameter('name'));

  const onUpdateName = useCallback(
    (newName: StringOrTemplate) => {
      onUpdatePreviewBuilder({
        ...previewBuilder,
        name: newName,
      });
    },
    [previewBuilder, onUpdatePreviewBuilder],
  );

  const onAppendName = useOnSelectCreateExpressionRef(
    useCallback(
      (ref) => onUpdateName(appendExpressionRef(previewBuilder.name, ref)),
      [onUpdateName, previewBuilder.name],
    ),
    StringLikeResults,
  );

  /*
   * Description.
   */

  const descriptionParam = useConstant(() => buildStringTemplateParameter('description'));

  const onUpdateDescription = useCallback(
    (newDescription: StringOrTemplate | undefined) => {
      onUpdatePreviewBuilder({
        ...previewBuilder,
        description: newDescription,
      });
    },
    [previewBuilder, onUpdatePreviewBuilder],
  );

  const onAppendDescription = useOnSelectCreateExpressionRef(
    useCallback(
      (ref) => onUpdateDescription(appendExpressionRef(previewBuilder.description, ref)),
      [onUpdateDescription, previewBuilder.description],
    ),
    StringLikeResults,
  );

  /*
   * Canonical URL.
   */

  const canonicalUrlParam = useConstant(() => buildStringTemplateParameter('canonicalUrl'));

  const onUpdateCanonicalUrl = useCallback(
    (newCanonicalUrl: StringOrTemplate | undefined) => {
      onUpdatePreviewBuilder({
        ...previewBuilder,
        canonicalUrl: newCanonicalUrl,
      });
    },
    [onUpdatePreviewBuilder, previewBuilder],
  );

  const onAppendCanonicalUrl = useOnSelectCreateExpressionRef(
    useCallback(
      (ref) => onUpdateCanonicalUrl(appendExpressionRef(previewBuilder.canonicalUrl, ref)),
      [onUpdateCanonicalUrl, previewBuilder.canonicalUrl],
    ),
    StringLikeResults,
  );

  /*
   * Status.
   */

  const statusParam = useConstant(() => buildStringTemplateParameter('status'));

  const onUpdateStatus = useCallback(
    (newStatus: StringOrTemplate | undefined) => {
      onUpdatePreviewBuilder({
        ...previewBuilder,
        status: newStatus,
      });
    },
    [onUpdatePreviewBuilder, previewBuilder],
  );

  const onAppendStatus = useOnSelectCreateExpressionRef(
    useCallback(
      (ref) => onUpdateStatus(appendExpressionRef(previewBuilder.status, ref)),
      [onUpdateStatus, previewBuilder.status],
    ),
    StringLikeResults,
  );

  /*
   * Owner.
   */

  const onUpdateOwner = useCallback(
    (newOwnerId: TeammateIdOrRef | null) => {
      if (isExpressionRef(newOwnerId)) {
        return;
      }

      onUpdatePreviewBuilder({
        ...previewBuilder,
        ownerId: newOwnerId ?? undefined,
      });
    },
    [onUpdatePreviewBuilder, previewBuilder],
  );

  /*
   * Render.
   */

  const collapsableParameters = useMemo(() => {
    const canonicalUrlNode = (
      <StyledParameterLineDiv key="canonicalUrl">
        <StyledParameterTitleLineDiv>
          <span>
            <Trans id="preview-builder-canonicalurl">Canonical URL</Trans>
          </span>
          <TooltipCoordinator
            iconColor={theme.greys.shade70}
            render={() => (
              <Trans id="preview-builder-canonicalurl-tooltip">
                Optional &#8212; Avoid duplicates when multiple URLs link to the same object.
              </Trans>
            )}
          />
        </StyledParameterTitleLineDiv>
        <InputStringTemplateParameter
          stringParameter={canonicalUrlParam}
          value={previewBuilder.canonicalUrl}
          onValueChange={onUpdateCanonicalUrl}
          isDisabled={isReadOnly}
          expressions={expressions}
          onSelectCreateExpression={onAppendCanonicalUrl}
        />
      </StyledParameterLineDiv>
    );

    const statusNode = (
      <StyledParameterLineDiv key="status">
        <StyledParameterTitleLineDiv>
          <span>
            <Trans id="preview-builder-status">Status</Trans>
          </span>
          <TooltipCoordinator
            iconColor={theme.greys.shade70}
            render={() => (
              <Trans id="preview-builder-status-tooltip">
                Optional &#8212; Status of the object in the external system. You can use this to trigger
                workflows when the status of an object changes.
              </Trans>
            )}
          />
        </StyledParameterTitleLineDiv>
        <InputStringTemplateParameter
          stringParameter={statusParam}
          value={previewBuilder.status}
          onValueChange={onUpdateStatus}
          isDisabled={isReadOnly}
          expressions={expressions}
          onSelectCreateExpression={onAppendStatus}
        />
      </StyledParameterLineDiv>
    );

    // @TODO: Support expression in owner node.
    const ownerNode = (
      <StyledParameterLineDiv key="owner">
        <StyledParameterTitleLineDiv>
          <span>
            <Trans id="preview-builder-owner">Owner</Trans>
          </span>
          <TooltipCoordinator
            iconColor={theme.greys.shade70}
            render={() => (
              <Trans id="preview-builder-owner-tooltip">
                Optional &#8212; Teammate to whom the linked object belongs.
              </Trans>
            )}
          />
        </StyledParameterTitleLineDiv>
        <TeammateParameter
          selectedItem={previewBuilder.ownerId ?? null}
          expressions={expressions}
          isDisabled={isReadOnly}
          onChange={onUpdateOwner}
        />
      </StyledParameterLineDiv>
    );

    return [canonicalUrlNode, statusNode, ownerNode];
  }, [
    canonicalUrlParam,
    expressions,
    isReadOnly,
    onAppendCanonicalUrl,
    onAppendStatus,
    onUpdateCanonicalUrl,
    onUpdateOwner,
    onUpdateStatus,
    previewBuilder.canonicalUrl,
    previewBuilder.ownerId,
    previewBuilder.status,
    statusParam,
  ]);

  // Expand collapsable parameters if there is at least one parameter.
  const shouldExpandCollapsableParameters = Boolean(
    previewBuilder.canonicalUrl || previewBuilder.status || previewBuilder.status,
  );

  return (
    <RuleMultilineParameterCard
      title={<Trans id="previewer-preview-builder">Update link attached data</Trans>}
      icon={<Icon name={getExpressionTypeIcon(TopicResult).regular.filled} />}
      collapsableParameters={collapsableParameters}
      shouldExpandCollapsableParameters={shouldExpandCollapsableParameters}
    >
      <StyledParameterLineDiv>
        <StyledParameterTitleLineDiv>
          <Trans id="previewer-editor-link-type-label">Type</Trans>
          <i>*</i>
        </StyledParameterTitleLineDiv>
        <PreviewerLinkTypeInput linkType={linkType} onUpdate={onUpdateLinkType} isReadOnly={isReadOnly} />
      </StyledParameterLineDiv>

      <StyledParameterLineDiv>
        <StyledParameterTitleLineDiv>
          <Trans id="preview-builder-name">Name</Trans>
          <i>*</i>
        </StyledParameterTitleLineDiv>
        <InputStringTemplateParameter
          stringParameter={nameParam}
          value={previewBuilder.name}
          onValueChange={onUpdateName}
          isDisabled={isReadOnly}
          expressions={expressions}
          onSelectCreateExpression={onAppendName}
        />
      </StyledParameterLineDiv>

      <StyledParameterLineDiv>
        <StyledParameterTitleLineDiv>
          <Trans id="preview-builder-description">Description</Trans>
        </StyledParameterTitleLineDiv>
        <InputStringTemplateParameter
          stringParameter={descriptionParam}
          value={previewBuilder.description}
          onValueChange={onUpdateDescription}
          isDisabled={isReadOnly}
          expressions={expressions}
          onSelectCreateExpression={onAppendDescription}
        />
      </StyledParameterLineDiv>
    </RuleMultilineParameterCard>
  );
};
